import { useMemo } from 'react';
import {
  EnhancedFieldConfig,
  EnhancedGroupedSection,
  FormSchemaConfig,
} from '../../types/form/schema.types';
import { FormState } from '../../types';
import { validateRequired } from '../Utils';
import { UseAuth } from '../../../../../contexts/AuthContext';
import {
  getSpoofUserHeader,
  isManager,
} from '../../../../../utils/permissions';
import {
  OVERTIME_FIELD_LABELS,
  OVERTIME_SECTION_KEYS,
  OVERTIME_SECTION_TITLES,
  YES_NO_OPTIONS,
} from './constants';

const isNoAnswer = (value: unknown): boolean =>
  String(value ?? '').trim().toLowerCase() === 'no';

export const useOvertimeApprovalLineManagerSchema = (
  formState: FormState
): FormSchemaConfig => {
  const { currentUser, spoofedUser } = UseAuth();

  return useMemo<FormSchemaConfig>(() => {
    let isEditAllowed = false;
    if (
      formState.data?.userAssignedSections?.includes(
        OVERTIME_SECTION_KEYS.lineManager
      ) === true
    ) {
      isEditAllowed = true;
    } else {
      isEditAllowed = isManager(
        formState.data?.line_manager,
        currentUser,
        getSpoofUserHeader(spoofedUser)
      );
    }

    const isSectionComplete =
      formState.completedSections?.includes(OVERTIME_SECTION_KEYS.lineManager) ||
      false;

    const isDisabled = isSectionComplete || !isEditAllowed;

    const lineManagerSection: EnhancedGroupedSection = {
      layout: 'single',
      isGrouped: true,
      isGroupedAccordion: false,
      sections: [
        {
          title: {
            text: OVERTIME_SECTION_TITLES.lineManager,
          },
          layout: 'single',
          fields: [
            {
              type: 'radio',
              name: 'confirm_approval_overtime_request',
              label: OVERTIME_FIELD_LABELS.confirmApproval,
              options: [...YES_NO_OPTIONS],
              required: true,
              validate: (value) =>
                !value
                  ? `Please select ${OVERTIME_FIELD_LABELS.confirmApproval}`
                  : undefined,
              disabled: isDisabled,
            },
            {
              type: 'textArea',
              name: 'line_manager_comments',
              label: OVERTIME_FIELD_LABELS.comments,
              required: isNoAnswer(
                formState.data?.confirm_approval_overtime_request
              ),
              validate: (value: unknown) => {
                if (
                  !isNoAnswer(
                    formState.data?.confirm_approval_overtime_request
                  )
                ) {
                  return undefined;
                }
                return validateRequired(value, OVERTIME_FIELD_LABELS.comments);
              },
              disabled: isDisabled,
              dependencies: [
                {
                  type: 'require',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'confirm_approval_overtime_request',
                      operator: 'equals',
                      value: 'no',
                    },
                  ],
                },
              ],
            } as EnhancedFieldConfig,
          ],
        },
      ],
      extras: { grouped: true, evenSpacing: true },
    };

    return {
      sections: [lineManagerSection],
      dependencies: {
        showWhen: [
          {
            field: OVERTIME_SECTION_KEYS.requestDetails,
          },
        ],
      },
    };
  }, [currentUser, formState, spoofedUser]);
};
